/** @prettier */
import React, { useMemo, useState, useEffect } from "react";
import classNames from "classnames";
import { Panel, ListGroup, ListGroupItem } from "react-bootstrap";
import { User, Subscription } from "../services/qbankApi";
import { Chart } from "../util/ChartJsChart";
import ProgressBar from "../Test/View/ProgressBar";
import {
    ScoreBreakdownItem,
    buildChartConfig,
    calcPercentage,
} from "../util/scoreWidget";
import TestProgressTable, { TestProgressTableProps } from "./TestProgressTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getDashboardData, DashboardData } from "./dashboardApi";
import ListGroupLinkItem from "../util/ListGroupLinkItem";
import "./StudentDashboard.css";

type StudentDashboardProps = {
    me: User;
    activeSubscription: Subscription;
};

type LoadState<T> =
    | { state: "loading" }
    | { state: "loaded"; data: T }
    | { state: "error" };

const StudentDashboard = ({
    me,
    activeSubscription,
}: StudentDashboardProps) => {
    const [loadState, setLoadState] = useState<LoadState<DashboardData>>({
        state: "loading",
    });
    useEffect(() => {
        getDashboardData(me.uuid, activeSubscription.product.uuid)
            .then((data) => setLoadState({ state: "loaded", data }))
            .catch((err) => {
                console.error(err);
                setLoadState({ state: "error" });
            });
    }, [me, activeSubscription]);
    const productName = activeSubscription.product.name;
    return (
        <div id="student-dashboard">
            <h4 className="dashboard-header">
                {productName} Dashboard - Welcome {me.firstName} {me.lastName}
            </h4>
            {loadState.state === "loading" ? (
                <div className="sdb-loading">
                    <FontAwesomeIcon icon="spinner" size="4x" spin />
                </div>
            ) : loadState.state === "error" ? (
                <div className="sdb-loading">
                    <span>
                        Uh oh, something went wrong loading the dashboard!
                    </span>
                </div>
            ) : (
                <StudentDashboardContent {...loadState.data} />
            )}
        </div>
    );
};
const StudentDashboardContent = (data: DashboardData) => (
    <div className="sdb-grid">
        <OverallScorePane
            correctQuestions={data.correctQuestions}
            incorrectQuestions={data.incorrectQuestions}
            unansweredQuestions={data.unansweredQuestions}
        />
        <ProgressPane
            totalQuestions={data.totalQuestions}
            unusedQuestions={data.unusedQuestions}
        />
        <MyTestsOverviewPane
            testsCreated={data.testsCreated}
            pausedTests={data.pausedTests}
        />
        <TestProgressPane data={data.pausedTestsList} />
        <LinksPane />
    </div>
);

type OverallScorePaneProps = Pick<
    DashboardData,
    "correctQuestions" | "incorrectQuestions" | "unansweredQuestions"
>;
const OverallScorePane = ({
    correctQuestions: correct,
    incorrectQuestions: incorrect,
    unansweredQuestions: unanswered,
}: OverallScorePaneProps) => {
    const [showingUnanswered, setShowingUnanswered] = useState(true);

    const total = showingUnanswered
        ? correct + incorrect + unanswered
        : correct + incorrect;

    const chartConfig = useMemo(
        () =>
            buildChartConfig("doughnut", [
                { value: correct, color: "#5cb85c", label: "Correct" },
                { value: incorrect, color: "#d9534f", label: "Incorrect" },
                ...(showingUnanswered
                    ? [
                          {
                              value: unanswered,
                              color: "#f0ad4e",
                              label: "Unanswered    ",
                          },
                      ]
                    : []),
            ]),
        [correct, incorrect, unanswered, showingUnanswered],
    );

    return (
        <Pane
            centered
            flush
            className="sdb-overall-score score-widget"
            title="Overall score"
        >
            <div className="score-widget-wrapper">
                <div className="score-widget-graph">
                    <Chart config={chartConfig} />
                </div>
                <div className="score-widget-percentage">
                    {calcPercentage(correct, total, 2)}%
                </div>
            </div>
            <div className="sdb-score-breakdown-wrapper">
                <div className="score-widget-breakdown">
                    <h5>{total} Scored Questions</h5>
                    <ScoreBreakdownItem
                        label="CORRECT"
                        labelStyle="success"
                        count={correct}
                        total={total}
                    />
                    <ScoreBreakdownItem
                        label="INCORRECT"
                        labelStyle="danger"
                        count={incorrect}
                        total={total}
                    />
                    {showingUnanswered && (
                        <ScoreBreakdownItem
                            label="UNANSWERED"
                            labelStyle="warning"
                            count={unanswered}
                            total={total}
                        />
                    )}
                </div>
                <span>
                    <input
                        type="checkbox"
                        checked={showingUnanswered}
                        onChange={(e) =>
                            setShowingUnanswered(e.currentTarget.checked)
                        }
                    />
                    {showingUnanswered
                        ? " Hide Unanswered"
                        : " Show Unanswered"}
                </span>
            </div>
        </Pane>
    );
};

type StatProps = {
    value: number;
    title: string;
};
const Stat = ({ value, title }: StatProps) => (
    <div className="sdb-stat">
        <div className="sdb-stat-value">{value}</div>
        <div className="sdb-stat-label">{title}</div>
    </div>
);

type MyTestsOverviewPaneProps = Pick<
    DashboardData,
    "testsCreated" | "pausedTests"
>;
const MyTestsOverviewPane = ({
    testsCreated,
    pausedTests,
}: MyTestsOverviewPaneProps) => (
    <Pane title="Overview of My Tests">
        <div className="sdb-stats">
            <Stat value={testsCreated} title="Created tests" />
            <Stat value={pausedTests} title="Paused tests" />
        </div>
    </Pane>
);
type ProgressPaneProps = Pick<
    DashboardData,
    "totalQuestions" | "unusedQuestions"
>;
const ProgressPane = ({
    totalQuestions,
    unusedQuestions,
}: ProgressPaneProps) => (
    <Pane flush title="QBank progress">
        <div className="sdb-qbank-progress">
            <div className="sdb-stats">
                <Stat value={totalQuestions} title="Total questions" />
                <Stat value={unusedQuestions} title="Unused questions" />
            </div>
            <ProgressBar percent={1 - unusedQuestions / totalQuestions} />
        </div>
    </Pane>
);

const testProgressTitle = "Paused tests";
const TestProgressPane = ({ data }: TestProgressTableProps) =>
    data.length ? (
        <Pane flush title={testProgressTitle}>
            <TestProgressTable data={data} />
        </Pane>
    ) : (
        <Pane centered title={testProgressTitle}>
            <div className="sdb-no-tests">No tests in progress</div>
        </Pane>
    );

const LinksPane = () => (
    <Pane title="Quick Links" flush>
        <ListGroup>
            <ListGroupLinkItem href="/tests/create">
                <FontAwesomeIcon icon="plus" />
                Create Test
            </ListGroupLinkItem>
            <ListGroupItem disabled>
                <FontAwesomeIcon icon="search" />
                QBank Store
            </ListGroupItem>
            <ListGroupItem disabled>
                <FontAwesomeIcon icon="comment-alt" />
                Submit Feedback
            </ListGroupItem>
        </ListGroup>
    </Pane>
);

type PaneProps = {
    title: string;
    children: React.ReactNode;
    className?: string;
    /** Whether to add padding: panes with ListGroups or Tables shouldn't add padding */
    flush?: boolean;
    /** Whether to center the contents in the padding */
    centered?: boolean;
};
const Pane = ({
    title,
    children,
    className,
    flush = false,
    centered = false,
}: PaneProps) => (
    <div
        className={classNames(
            "sdb-pane",
            !flush && "padded",
            centered && "centered",
            className,
        )}
    >
        <h5>{title}</h5>
        <Panel>{children}</Panel>
    </div>
);
export default StudentDashboard;
